import { useCallback, useEffect, useState } from 'react';
import { errorMessage } from '../data/repo';
import { parseRoute, type Route } from '../lib/route';

export interface AsyncState<T> {
  loading: boolean;
  data: T | undefined;
  error: string | null;
  reload: () => void;
}

/** Load data for a view. Errors stay local to the view that asked, so one failure never blanks the app. */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [tick, setTick] = useState(0);
  const [state, setState] = useState<{ loading: boolean; data: T | undefined; error: string | null }>({
    loading: true, data: undefined, error: null,
  });
  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    fn().then(
      (data) => live && setState({ loading: false, data, error: null }),
      (e) => live && setState((s) => ({ loading: false, data: s.data, error: errorMessage(e) })),
    );
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, reload };
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.hash));
  useEffect(() => {
    const on = () => setRoute(parseRoute(window.location.hash));
    window.addEventListener('hashchange', on);
    window.addEventListener('popstate', on);
    return () => { window.removeEventListener('hashchange', on); window.removeEventListener('popstate', on); };
  }, []);
  return route;
}

/** Close on Escape. */
export function useEscape(onEscape: () => void, active = true) {
  useEffect(() => {
    if (!active) return;
    const on = (e: KeyboardEvent) => { if (e.key === 'Escape') onEscape(); };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, [onEscape, active]);
}
